import type { SessionSummary } from '../../engine/session';
import type { Score } from '../../music/model';
import { missionById, type Award } from '../../store/missions';
import { isPassed, passBlockers } from '../../store/progress';
import { handLabel, percent } from '../format';
import { INPUT_LABELS } from '../inputControl';
import { href } from '../router';

interface Props {
  summary: SessionSummary;
  score: Score;
  /** Récompenses obtenues pendant cette séance. */
  awards?: readonly Award[];
  onReplay: () => void;
  onSwitchToTempo: () => void;
}

export function SummaryPanel({ summary, score, awards = [], onReplay, onSwitchToTempo }: Props) {
  const passed = isPassed(summary, score.tempo);
  const wait = summary.mode === 'wait';
  const blockers = wait ? [] : passBlockers(summary, score.tempo);
  const handsText = summary.hands.length >= 2 ? 'mains ensemble' : summary.hands.map((h) => handLabel(h).toLowerCase()).join(', ');
  const sourceText = summary.sources.length === 0 ? 'aucune note jouée' : summary.sources.map((s) => INPUT_LABELS[s]).join(' + ');
  const rows: [string, number, string][] = wait
    ? [
        ['Pas réussis du premier coup', summary.firstTrySteps, 'ok'],
        ['Fausses notes', summary.wrong, 'bad'],
        ['Mauvaise octave', summary.wrongOctave, 'bad'],
        ['Notes en trop', summary.extra, 'bad'],
        ['Détections incertaines, non comptées', summary.uncertainEvents, 'unsure'],
      ]
    : [
        ['Justes, en rythme', summary.correct, 'ok'],
        ['En avance', summary.early, 'timing'],
        ['En retard', summary.late, 'timing'],
        ['Fausses notes', summary.wrong, 'bad'],
        ['Mauvaise octave', summary.wrongOctave, 'bad'],
        ['Notes en trop', summary.extra, 'bad'],
        ['Manquées', summary.missed, 'bad'],
        ['Notes non évaluables (captation)', summary.unevaluated, 'unsure'],
        ['Détections incertaines, non comptées', summary.uncertainEvents, 'unsure'],
      ];
  const advice: string[] = [];
  if (summary.unevaluated > 0 || summary.uncertainEvents > 0) {
    const mic = summary.sources.includes('mic') || summary.sources.includes('synth');
    advice.push(
      mic
        ? 'Des détections incertaines n’ont pas été comptées : rapprochez l’appareil du piano, réduisez le bruit, jouez les notes bien détachées, ou passez en MIDI.'
        : 'Des détections incertaines n’ont pas été comptées.',
    );
  }
  if (summary.wrongOctave > 0) advice.push('Bonne note mais mauvaise octave : repérez le do central (marqué sur le clavier affiché) avant de commencer.');
  if (!wait && summary.early + summary.late > summary.correct) advice.push('Beaucoup de notes hors rythme : baissez le tempo, ou vérifiez la compensation de latence dans les réglages si vous utilisez le micro.');
  if (!wait && summary.missed > 0 && summary.missed >= summary.expected / 3) advice.push('Beaucoup de notes manquées : repassez en mode attente pour mémoriser le passage, puis revenez au tempo.');

  return (
    <section className="summary" aria-live="polite">
      <div className="summary-head">
        <h2>{passed ? 'Exercice réussi' : wait ? 'Découverte terminée' : 'Bilan'}</h2>
        <p className="muted">
          {wait ? 'Mode attente' : `Mode tempo, ♩ = ${summary.tempo}`} · {summary.expected} notes attendues · {handsText} · entrée : {sourceText}
        </p>
      </div>
      {awards.length > 0 && (
        <div className="summary-awards" aria-label="Récompenses de la séance">
          {awards.map((a) => {
            const def = missionById(a.id);
            return (
              <span key={`${a.id}-${a.tier}`} className={a.tier === 'instrument' ? 'chip passed' : 'chip started'}>
                {def.icon} {def.badge[a.tier]} · +{a.xp} XP
              </span>
            );
          })}
        </div>
      )}
      <div className="summary-figures">
        <div>
          <span className="figure">{percent(summary.accuracyPitch)}</span>
          <span className="figure-label">{wait ? 'pas réussis du premier coup' : 'notes justes, partie évaluable'}</span>
        </div>
        {!wait && (
          <div>
            <span className="figure">{percent(summary.accuracyTiming)}</span>
            <span className="figure-label">en rythme (± tolérance)</span>
          </div>
        )}
        {!wait && (
          <div>
            <span className="figure">{percent(summary.precision)}</span>
            <span className="figure-label">précision : notes jouées qui étaient attendues</span>
          </div>
        )}
        {summary.coverage < 1 && (
          <div>
            <span className="figure warn">{percent(summary.coverage)}</span>
            <span className="figure-label">{wait ? 'frappes évaluables' : 'notes évaluables (captation)'}</span>
          </div>
        )}
      </div>
      <table className="summary-table">
        <tbody>
          {rows.map(([label, value, cls]) => (
            <tr key={label} className={value > 0 ? cls : 'zero'}>
              <td>{label}</td>
              <td className="num">{value}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {blockers.length > 0 && (
        <div className="blockers">
          <strong>Non validé</strong>
          <ul>
            {blockers.map((b) => (
              <li key={b}>{b}</li>
            ))}
          </ul>
        </div>
      )}
      {advice.length > 0 && (
        <ul className="advice">
          {advice.map((a) => (
            <li key={a}>{a}</li>
          ))}
        </ul>
      )}
      <div className="summary-actions">
        <button type="button" className="btn primary" onClick={onReplay}>
          Rejouer
        </button>
        {wait && summary.accuracyPitch >= 0.9 && (
          <button type="button" className="btn" onClick={onSwitchToTempo}>
            Passer au tempo imposé
          </button>
        )}
        <a className="btn ghost" href={href('catalogue')}>
          Catalogue
        </a>
      </div>
    </section>
  );
}
